'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/cn'

const Accordion = AccordionPrimitive.Root

const accordionItemVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b border-border',
      minimal: '',
      card: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>, VariantProps<typeof accordionItemVariants> {}

const AccordionItem = React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Item>, AccordionItemProps>(
  ({ className, variant, ...props }, ref) => (
    <AccordionPrimitive.Item ref={ref} className={cn(accordionItemVariants({ variant }), className)} {...props} />
  ),
)
AccordionItem.displayName = 'AccordionItem'

const accordionTriggerVariants = cva(
  'flex flex-1 items-center justify-between font-medium transition-all hover:cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
  {
    variants: {
      variant: {
        default: 'py-4 hover:underline',
        minimal: 'py-0!',
        card: 'bg-transparent border border-border rounded-lg px-3 py-2 text-xs font-semibold tracking-wide hover:no-underline data-[state=open]:rounded-b-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface AccordionTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
    VariantProps<typeof accordionTriggerVariants> {
  hideChevron?: boolean
}

const AccordionTrigger = React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  ({ className, children, variant, hideChevron = false, ...props }, ref) => (
    <AccordionPrimitive.Header className='flex'>
      <AccordionPrimitive.Trigger ref={ref} className={cn(accordionTriggerVariants({ variant }), className)} {...props}>
        {children}
        {!hideChevron && <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200' />}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  ),
)
AccordionTrigger.displayName = 'AccordionTrigger'

const accordionContentVariants = cva('', {
  variants: {
    variant: {
      default: 'pb-4 pt-0',
      minimal: 'pt-2 pb-0',
      card: 'flex flex-col gap-4 rounded-b-2xl border-x border-b border-border p-3',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface AccordionContentProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>,
    VariantProps<typeof accordionContentVariants> {}

const AccordionContent = React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Content>, AccordionContentProps>(
  ({ className, children, variant, ...props }, ref) => (
    <AccordionPrimitive.Content
      ref={ref}
      className='overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
      {...props}
    >
      <div className={cn(accordionContentVariants({ variant }), className)}>{children}</div>
    </AccordionPrimitive.Content>
  ),
)
AccordionContent.displayName = 'AccordionContent'

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  accordionItemVariants,
  accordionTriggerVariants,
  accordionContentVariants,
}
